import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import imageHeaderBanner from "./../../../assets/images/domain/annex/annex.jpg";

import H1 from "../../../components/Titles/h1";
import Button from "../../../components/Buttons/button";
import DescriptionSplit from "../../../components/Description/description-split";
import BackgroundImage from "../../../components/Banner/background-header";
import RoomDetails from "../components/room-details";
import { Helmet } from "react-helmet";

import imageData from './../../../utils/imports/import-images.json';

const listOfRooms = ['livingRoom', 'kitchen', 'diningRoom'];
const listOfSuits = ['saphirSuit', 'jadeSuit', 'aventurineSuit', 'amethysteSuit', 'agateSuit'];

export default function AnnexDetails() {
    const { t } = useTranslation();

    const scrollTargetRooms = useRef(null);
    const scrollTargetSuits = useRef(null);
    const scrollTargetOutdoors = useRef(null);

    const [headerHeight, setHeaderHeight] = useState(0);

    const extractImages = (list, category) => {
        return list.reduce((acc, item) => {
            if (imageData['annex'][category][item]) {
                acc[item] = imageData['annex'][category][item];
            }
            return acc;
        }, {});
    };

    const roomImages = extractImages(listOfRooms, 'rooms');
    const suitImages = extractImages(listOfSuits, 'suits');
    const outdoorImages = imageData?.annex?.outdoors || [];

    useEffect(() => {
        const updateHeaderHeight = () => {
            const header = document.querySelector('header');
            if (header) setHeaderHeight(header.offsetHeight);
        };

        updateHeaderHeight();
        window.addEventListener('resize', updateHeaderHeight);
        return () => window.removeEventListener('resize', updateHeaderHeight);
    }, []);

    const handleScrollToDiv = (ref) => {
        if (ref.current) {
            const top = ref.current.getBoundingClientRect().top + window.scrollY - headerHeight - 10;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    return (
        <div>
            <Helmet>
                <title>{t('annexDetails.page')}</title>
                <meta name="description" content={t('annexDetails.description')} />
                <meta name="keywords" content={t('annexDetails.keywords')} />
            </Helmet>

            <div className="absolute top-0 w-full">
                <BackgroundImage image={imageHeaderBanner} alt={t('reservation.altBackground')} />
            </div>

            <div className="absolute left-1/2 transform -translate-x-1/2 bottom-12 md:bottom-24 w-4/5 md:w-3/5 text-center">
                <H1 title={t('annexDetails.title')} />

                <div className="mt-4 mb-6">
                    <DescriptionSplit description={t('annexDetails.titleDescription')} text='center' />
                </div>

                <div className="flex flex-col md:flex-row flex-wrap items-center justify-center gap-4 md:gap-10">
                    <Button text={t('annexDetails.buttonLivingRooms')} onClick={() => handleScrollToDiv(scrollTargetRooms)} />
                    <Button text={t('annexDetails.buttonSuits')} onClick={() => handleScrollToDiv(scrollTargetSuits)} />
                    <Button text={t('annexDetails.buttonOutdoors')} onClick={() => handleScrollToDiv(scrollTargetOutdoors)} />
                </div>
            </div>

            <div className="max-w-[2000px] m-auto">
                <div ref={scrollTargetRooms} className="relative mt-[100vh]">
                    {listOfRooms.map((room, index) => (
                        <RoomDetails
                            key={room}
                            data={t(`annexDetails.${room}`, { returnObjects: true })}
                            imagesPaths={roomImages[room] || []}
                            carouselOn={index % 2 === 0 ? 'left' : 'right'}
                        />
                    ))}
                </div>

                <div ref={scrollTargetSuits}>
                    <div className="my-8 md:my-12 bg-secondary py-4 md:py-6 px-12 md:px-24 w-fit m-auto">
                        <H1 title={t("annexDetails.suits")} fontWeight="semibold" theme="white" />
                    </div>

                    {listOfSuits.map((suit, index) => (
                        <RoomDetails
                            key={suit}
                            data={t(`annexDetails.${suit}`, { returnObjects: true })}
                            imagesPaths={suitImages[suit] || []}
                            carouselOn={index % 2 === 0 ? 'left' : 'right'}
                        />
                    ))}
                </div>

                <div ref={scrollTargetOutdoors}>
                    <div className="my-8 md:my-12 bg-secondary py-4 md:py-6 px-12 md:px-24 w-fit m-auto">
                        <H1 title={t("annexDetails.outdoors.title")} fontWeight="semibold" theme="white" />
                    </div>

                    <RoomDetails
                        data={t('annexDetails.outdoors', { returnObjects: true })}
                        imagesPaths={outdoorImages}
                        carouselOn="left"
                    />
                </div>
            </div>
        </div>
    );
}
